import { AlertTriangle, CheckCircle2, FileWarning, Wrench } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import type { NormalizationResult } from '@/features/import/normalizer';

function StatTile({ label, value, tone }: { label: string; value: number; tone?: 'default' | 'warning' | 'error' | 'success' }) {
  const className =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50 dark:border-amber-950 dark:bg-amber-950/30'
      : tone === 'error'
      ? 'border-rose-200 bg-rose-50 dark:border-rose-950 dark:bg-rose-950/30'
      : tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-950 dark:bg-emerald-950/30'
      : 'border-slate-200 bg-slate-50/70 dark:border-slate-800 dark:bg-slate-900/40';

  return (
    <div className={`rounded-2xl border p-3 ${className}`}>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

export function NormalizationReportPanel({ report }: { report: NormalizationResult | null }) {
  if (!report) return null;

  const sampleRejected = report.rejectedRows.slice(0, 5);
  const sampleManualReview = report.rows.filter((row) => row.manualReview).slice(0, 5);

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-lg font-semibold">
            <FileWarning className="h-5 w-5" /> Нормализация данных
          </div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            До построения графа строки проходят нормализацию единиц, пустых значений, кодировки и финансовых связок.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge><CheckCircle2 className="h-3.5 w-3.5" /> В графе: {report.stats.acceptedToGraph}</Badge>
          <Badge><AlertTriangle className="h-3.5 w-3.5" /> Manual review: {report.stats.manualReview}</Badge>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        <StatTile label="Всего строк" value={report.stats.total} />
        <StatTile label="Принято в граф" value={report.stats.acceptedToGraph} tone="success" />
        <StatTile label="Отклонено" value={report.stats.rejected} tone={report.stats.rejected > 0 ? 'error' : 'default'} />
        <StatTile label="Warnings" value={report.stats.warnings} tone={report.stats.warnings > 0 ? 'warning' : 'default'} />
        <StatTile label="Errors" value={report.stats.errors} tone={report.stats.errors > 0 ? 'error' : 'default'} />
        <StatTile label="Auto-fixed" value={report.stats.autoFixed} />
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
          <div className="flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4" /> Отклонённые строки
          </div>
          {sampleRejected.length === 0 ? (
            <div className="mt-3 text-sm text-slate-500 dark:text-slate-400">Все строки можно использовать для построения графа.</div>
          ) : (
            <div className="mt-3 space-y-3">
              {sampleRejected.map((row) => (
                <div key={`rejected-${row.index}`} className="rounded-2xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-950 dark:bg-rose-950/30">
                  <div className="text-sm font-medium">Строка #{row.index + 1}</div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600 dark:text-slate-300">
                    {row.issues.slice(0, 4).map((issue, index) => (
                      <li key={`${row.index}-${issue.code}-${index}`}>{issue.field}: {issue.message}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Wrench className="h-4 w-4" /> Строки с ручной проверкой
          </div>
          {sampleManualReview.length === 0 ? (
            <div className="mt-3 text-sm text-slate-500 dark:text-slate-400">Подозрительных строк не найдено.</div>
          ) : (
            <div className="mt-3 space-y-3">
              {sampleManualReview.map((row) => (
                <div key={`manual-${row.index}`} className="rounded-2xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-950 dark:bg-amber-950/30">
                  <div className="text-sm font-medium">Строка #{row.index + 1}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {row.issues.slice(0, 4).map((issue, index) => (
                      <Badge key={`${row.index}-${issue.code}-${index}`} className="border-amber-200 bg-white text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                        {issue.field}: {issue.code}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
